'use strict';

const cssConstants = require('../utils/cssConstants');
const errorManager = require('./error');
const vatHelper = require('../utils/vatNumberHelper');
const siretHelper = require('../utils/siretNumberHelper');
const invoicingHelper = require('../utils/invoicingElectronicAddressHelper');
var invalidFields = [];

/**
 * Runs a per-field validation against every ACTIVE address block held by the container.
 *
 * The checkout renders the shipping and billing blocks inside a SINGLE form
 * (checkout/createAddressForm.isml). A selector spanning the whole form therefore returns
 * the fields of BOTH blocks: jQuery reads the value of the first match only, but applies
 * every write (addClass, text, showError) to all of them — so one invalid shipping value
 * paints its error on the billing block too. Every per-field check must be scoped to a
 * single block, which is what this helper enforces.
 *
 * A block whose inputs are all disabled is inactive — the billing block when "same as
 * shipping" is ticked, the shipping block when a saved address is used. It still holds
 * values that are never submitted, so it is skipped. A container without address blocks
 * (registration, profile, address book) holds one set of fields and is passed through.
 * @param {jQuery} $container - The form, or a single address block.
 * @param {Function} validateBlock - Receives one block, returns false when it is invalid.
 * @returns {boolean} true when every active block passed.
 */
function eachAddressBlock($container, validateBlock) {
    var $blocks = $container.find('.address-block');
    var valid = true;

    ($blocks.length ? $blocks : $container).each(function () {
        var $block = $(this);
        if (!$block.find('input:not(:disabled)').length) {
            return;
        }
        if (!validateBlock($block)) {
            valid = false;
        }
    });

    return valid;
}

/**
 * Validate whole form. Requires `this` to be set to form object
 * @param {jQuery.event} event - Event to be canceled if form is invalid.
 * @returns {boolean} - Flag to indicate if form is valid
 */
function validateForm(event) {
    var valid = true;
    var $formGroup;
    var $fieldError;

    // eslint-disable-next-line no-use-before-define
    clearForm($(this));

    // Validates customer type is selected on registration form
    var $customerTypeRadios = $(this).find('.js-register-customer-type');
    if ($customerTypeRadios.length && !$customerTypeRadios.filter(':checked').length) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
        $formGroup = $customerTypeRadios.first().parents('.form-group');
        $fieldError = $formGroup.find('.invalid-feedback');
        $formGroup.addClass(cssConstants.FORM_GROUP_INVALID);
        $fieldError.text($customerTypeRadios.first().data('missing-error'));
        return false;
    }

    // Validates the company fields (company name, SIRET, invoicing electronic address, VAT number)
    // when the selected customer type is "company". Shared with the checkout billing block.
    // eslint-disable-next-line no-use-before-define
    if (!validateCompanyFields($(this), event)) {
        return false;
    }

    // Validates contact e-mail confirmation (individual customers) before form submit
    var $emailConfirmField = $(this).find('.js-email-confirm');
    if (
        $emailConfirmField.length &&
        !$emailConfirmField.closest('.js-individual-field').hasClass('d-none') &&
        $emailConfirmField.val() !== $(this).find('.js-email').val()
    ) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
        $formGroup = $emailConfirmField.closest('.form-group');
        $fieldError = $formGroup.find('.invalid-feedback');
        $emailConfirmField.addClass(cssConstants.INVALID);
        $formGroup.addClass(cssConstants.FORM_GROUP_INVALID);
        $fieldError.text($emailConfirmField.data('confirm-error'));
        return false;
    }

    // Validates password confirmation value before form submit even the field validity is valid
    var $pwdConfirmField = $(this).find('.js-password-confirm');
    if($pwdConfirmField.length && $pwdConfirmField.val() !== $('.js-password').val()) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
        $formGroup = $pwdConfirmField.parents('.form-group');
        $fieldError = $formGroup.find('.invalid-feedback');
        $pwdConfirmField.addClass(cssConstants.INVALID);
        $formGroup.addClass(cssConstants.FORM_GROUP_INVALID);
        $fieldError.text($pwdConfirmField.data('confirm-error'));
        return false;
    }

    // New password must differ from the current one (mirrors server rule).
    var $currentPwdField = $(this).find('.js-current-password');
    var $newPwdField = $(this).find('.js-password');
    if ($currentPwdField.length && $newPwdField.length
        && $newPwdField.val() && $newPwdField.val() === $currentPwdField.val()) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
        $formGroup = $newPwdField.parents('.form-group');
        $fieldError = $formGroup.find('.invalid-feedback');
        $newPwdField.addClass(cssConstants.INVALID);
        $formGroup.addClass(cssConstants.FORM_GROUP_INVALID);
        $fieldError.text($newPwdField.data('sameascurrent-error'));
        return false;
    }

    // Validates postalcode and city values before form submit even the field validity is valid.
    // Scoped per address block: the checkout holds two blocks in one form, so a single
    // lookup would test the first block's row while flagging both.
    var postalCodeValid = eachAddressBlock($(this), function ($block) {
        var $postalCodeField = $block.find('.js-address-postalcode.editable-field');
        var $containerRow = $postalCodeField.closest('.form-group-row');

        if (!$containerRow.length || $containerRow.is('.valid')) {
            return true;
        }

        $containerRow.find('.form-group').addClass(cssConstants.FORM_GROUP_INVALID);
        errorManager.showError($postalCodeField, $postalCodeField.data('inconsistency-error'));

        var $cityField = $block.find('.js-address-city.editable-field');
        errorManager.showError($cityField, $cityField.data('inconsistency-error'));

        return false;
    });

    if (!postalCodeValid) {
        if (event) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }

        valid = false;
    }

    if (this.checkValidity && !this.checkValidity()) {
        // safari
        valid = false;
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
        $(this)
            .find('input, select, textarea')
            .each(function () {
                if (!this.validity.valid) {
                    $(this).trigger('invalid', this.validity);
                }
            });
    }
    return valid;
}

/**
 * Flags a field as invalid: marks the input and its form group, then shows the message.
 * Single place holding the invalid-field DOM contract for the company block.
 * @param {jQuery} $field - The input to flag.
 * @param {string} message - The message to display in the field's `.invalid-feedback`.
 * @returns {void}
 */
function markFieldInvalid($field, message) {
    var $formGroup = $field.closest('.form-group');
    $field.addClass(cssConstants.INVALID);
    $formGroup.addClass(cssConstants.FORM_GROUP_INVALID);
    $formGroup.find('.invalid-feedback').text(message);
}

/**
 * Validates the company fields (company name, SIRET, invoicing electronic address, VAT
 * number) of a SINGLE address block whose selected customer type is "company". Mirrors the
 * server helper addressHelpers.getCompanyFieldErrorKeys so the shopper gets immediate
 * feedback; the server re-validates as the authority.
 *
 * A disabled customer type means the whole block is inactive — the billing block when
 * "same as shipping" is ticked, the shipping block when a saved address is used. Such a
 * block still holds values that are never submitted, so it is skipped.
 * @param {jQuery} $block - A single address block, or a form holding one set of fields.
 * @returns {boolean} true when the block is not a company or all its company fields are valid.
 */
function validateCompanyBlock($block) {
    var $customerType = $block.find('.js-register-customer-type:checked, .js-address-customertype:checked');
    if (!$customerType.length || $customerType.val() !== 'company') {
        return true;
    }

    var $companyName = $block.find('.js-register-company-name, .js-address-company');
    var $siret = $block.find('.js-register-siret, .js-address-siret');
    var $invoicingElectronicAddress = $block.find('.js-register-invoicing-electronic-address, .js-address-invoicing-electronic-address');
    var $vatNumber = $block.find('.js-vat-number');
    var companyValid = true;

    if ($companyName.length && !$companyName.val().trim()) {
        markFieldInvalid($companyName, $companyName.data('missing-error'));
        companyValid = false;
    }

    // eslint-disable-next-line no-use-before-define
    if (!validateSiretField($siret)) {
        companyValid = false;
    }

    // eslint-disable-next-line no-use-before-define
    if (!validateInvoicingElectronicAddressField($invoicingElectronicAddress)) {
        companyValid = false;
    }

    // Complete VAT validation: per-country format + FR control key cross-checked against
    // the SIRET (mirrors the server helper). The server re-validates on submit.
    // eslint-disable-next-line no-use-before-define
    if (!validateVatField($vatNumber, $block.find('.js-siret'))) {
        companyValid = false;
    }

    return companyValid;
}

/**
 * Validates the company fields of every address block held by the container.
 *
 * The checkout wraps the shipping and billing blocks in a SINGLE form, so each block must
 * be validated on its own: one lookup spanning the whole form returns the fields of both
 * blocks, and jQuery would then read the first block's value while flagging both. A
 * container without address blocks (registration, profile, address book) is validated as
 * one block.
 * @param {jQuery} $container - The form, or a single address block.
 * @param {jQuery.event} [event] - Optional submit event to cancel when invalid.
 * @returns {boolean} true when every active block is valid.
 */
function validateCompanyFields($container, event) {
    var valid = eachAddressBlock($container, validateCompanyBlock);

    if (!valid && event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
    }

    return valid;
}

/**
 * Remove all validation. Should be called every time before revalidating form
 * @param {element} form - Form to be cleared
 * @returns {void}
 */
function clearForm(form) {
    $(form)
        .find('.form-control, .form-select, .form-check-input, .btn-check, .form-input')
        .filter(`.${cssConstants.INVALID}`)
        .removeClass(cssConstants.INVALID);
    $(form).find('.form-group.' + cssConstants.FORM_GROUP_INVALID).removeClass(cssConstants.FORM_GROUP_INVALID);
}

/**
 * Checks if the email value entered is correct format
 * @param {string} email - email string to check if valid
 * @returns {boolean} Whether email is valid
 */
function validateEmail(email) {
    // eslint-disable-next-line no-useless-escape
    var regex = /^(?:[\w]+){1}[+\w\.\-]*@(?:[\w\-]+)((\.(\w){2,9})+)$/;
    return regex.test(email);
}

/**
 * Complete client-side SIRET validation: 14-digit format then Luhn control key on the
 * SIREN and on the full SIRET. Mirrors the server helper so the shopper gets immediate
 * feedback; the server re-validates as a backstop.
 * @param {jQuery} $siret - the SIRET input (may be an empty selection)
 * @returns {boolean} true when the SIRET is valid (or the field is absent)
 */
function validateSiretField($siret) {
    if (!$siret.length) {
        return true;
    }
    var result = siretHelper.validate($siret.val());
    if (result.valid) {
        return true;
    }
    // reason -> data-attribute; server twin: SIRET_ERROR_KEYS in scripts/helpers/addressHelpers.js
    var attributes = { empty: 'missing-error', format: 'pattern-mismatch', checksum: 'checksum-error' };
    markFieldInvalid($siret, $siret.data(attributes[result.reason] || 'pattern-mismatch'));
    return false;
}

/**
 * Complete client-side validation of the electronic invoicing address: surface check of
 * the routing key once trimmed. Mirrors the server helper so the shopper gets immediate
 * feedback; the server re-validates as a backstop.
 * @param {jQuery} $invoicingElectronicAddress - the routing key input (may be an empty selection)
 * @returns {boolean} true when the routing key is valid (or the field is absent)
 */
function validateInvoicingElectronicAddressField($invoicingElectronicAddress) {
    if (!$invoicingElectronicAddress.length) {
        return true;
    }
    var result = invoicingHelper.validate($invoicingElectronicAddress.val());
    if (result.valid) {
        return true;
    }
    // reason -> data-attribute; server twin: INVOICING_ELECTRONIC_ADDRESS_ERROR_KEYS in scripts/helpers/addressHelpers.js
    var attributes = { empty: 'missing-error', format: 'pattern-mismatch' };
    markFieldInvalid($invoicingElectronicAddress, $invoicingElectronicAddress.data(attributes[result.reason] || 'pattern-mismatch'));
    return false;
}

/**
 * Complete client-side VAT validation: per-country structural format and, for France,
 * the control key cross-checked against the SIRET. Mirrors the server helper so the
 * shopper gets immediate feedback; the server re-validates as a backstop.
 * @param {jQuery} $vatNumber - the VAT input (may be an empty selection)
 * @param {jQuery} [$siret] - the companion SIRET input, used for the FR cross-check
 * @returns {boolean} true when the VAT number is valid (or the field is absent)
 */
function validateVatField($vatNumber, $siret) {
    if (!$vatNumber.length) {
        return true;
    }
    var siretValue = $siret && $siret.length ? $siret.val() : '';
    var result = vatHelper.validate($vatNumber.val(), siretValue);
    if (result.valid) {
        return true;
    }
    markFieldInvalid($vatNumber, result.reason === 'empty'
        ? $vatNumber.data('missing-error')
        : $vatNumber.data('pattern-mismatch'));
    return false;
}

/**
 * Scrolls to first invalid field on form
 * @param {Object} el - field has error
 */
function scrollToInvalid(el) {
    var $firstFieldError = el || '';

    if($firstFieldError.length) {
        var fieldPosY = $firstFieldError.offset().top;
        var windowPosY = $(window).scrollTop();
        if (windowPosY >= fieldPosY) {
            // if input is out of the view, page scrolls
            $('html, body').animate({
                scrollTop: fieldPosY - 40 // minus to include label
            }, 200);
        }
    }
}

module.exports = {
    validateDigitsOnlyFields: function () {
        $('body').on('input', '.js-digits-only', function () {
            this.value = this.value.replace(/[^0-9]/g, '');
        });
    },

    trimOnBlurFields: function () {
        // focusout, not blur: it bubbles, so the binding survives ajax-re-rendered forms.
        $('body').on('focusout', '.js-trim-on-blur', function () {
            this.value = this.value.trim();
        });
    },

    invalid: function () {
        $('form input, form select, form textarea').on('invalid', function (e) {
            e.preventDefault();
            this.setCustomValidity('');
            if (!this.validity.valid) {
                var validationMessage = this.validationMessage;
                var $formGroup = $(this).parents('.form-group');
                var $fieldError = $formGroup.find('.invalid-feedback');

                $(this).removeClass(cssConstants.VALID).addClass(cssConstants.INVALID);
                $formGroup.removeClass(cssConstants.VALID).addClass(cssConstants.FORM_GROUP_INVALID);

                if (this.validity.patternMismatch && $(this).data('pattern-mismatch')) {
                    validationMessage = $(this).data('pattern-mismatch');
                }

                // Client-side email pattern matching
                if (
                    $(this).attr('type') === 'email' &&
                    $(this).data('pattern-mismatch') &&
                    !validateEmail($(this).val)
                ) {
                    validationMessage = $(this).data('pattern-mismatch');
                }

                if (
                    this.validity.patternMismatch &&
                    $(this).attr('type') !== 'email' &&
                    $(this).data('pattern-mismatch')
                ) {
                    validationMessage = $(this).data('pattern-mismatch');
                }
                if (
                    (this.validity.rangeOverflow ||
                        this.validity.rangeUnderflow) &&
                    $(this).data('range-error')
                ) {
                    validationMessage = $(this).data('range-error');
                }
                if (
                    (this.validity.tooLong || this.validity.tooShort) &&
                    $(this).data('range-error')
                ) {
                    validationMessage = $(this).data('range-error');
                }
                if (
                    this.validity.valueMissing &&
                    $(this).data('missing-error')
                ) {
                    validationMessage = $(this).data('missing-error');
                }

                // Password confirmation field validation
                if (
                    !this.validity.valueMissing &&
                    $(this).attr('type') === 'password' &&
                    $(this).hasClass('js-password-confirm') &&
                    $(this).val() !== $('.js-password').val()
                ) {
                    validationMessage = $(this).data('confirm-error');
                }
                $fieldError.text(validationMessage);
                invalidFields.push($(this));
            }
        });
    },

    submit: function () {
        $('form').on('submit', function (e) {
            return validateForm.call(this, e);
        });
    },

    buttonClick: function () {
        $('body').on('click', 'form button[type="submit"], form input[type="submit"]',
            function () {
                // Focuses on first invalid input
                setTimeout(function(){
                    // Delayed since invalid input loads
                    if(invalidFields.length) {
                        scrollToInvalid(invalidFields[0]);
                        invalidFields = [];
                    }
                }, 500);
                // clear all errors when trying to submit the form
                clearForm($(this).parents('form'));
            }
        );
    },

    functions: {
        validateForm: function (form, event) {
            return validateForm.call(form, event || null);
        },
        validateVatField: function (container) {
            var $container = $(container);
            var $type = $container.find('.js-register-customer-type:checked, .js-address-customertype:checked');
            if ($type.val() !== 'company') {
                return true;
            }
            return validateVatField($container.find('.js-vat-number'), $container.find('.js-siret'));
        },
        validateCompanyFields: function (container, event) {
            return validateCompanyFields($(container), event || null);
        },
        clearForm: clearForm,
        scrollToInvalid: scrollToInvalid
    }
};
